import { supabaseUrl, supabaseAnonKey } from '../../config/supabaseConfig';
import { supabase } from '../../supabase';
import { sanitizeUrlForLogging } from './sanitizeUrlForLogging';

export function reportApiError(functionName, status, errorMessage) {
  reportError(
    { message: `[API] ${functionName}: ${errorMessage}`, stack: null },
    { component: `edge-fn:${functionName}` },
  );
}

// Client-side dedup: message → last reported timestamp (ms)
const recentMessages = new Map();
const DEDUP_WINDOW_MS = 60_000;

export function reportError(error, extra = {}) {
  try {
    const message = error?.message || String(error);

    // Client-side dedup: skip if same message within last 60 seconds
    const now = Date.now();
    const lastSeen = recentMessages.get(message);
    if (lastSeen !== undefined && now - lastSeen < DEDUP_WINDOW_MS) {
      return;
    }
    recentMessages.set(message, now);

    // Identity is derived server-side from the Bearer (RD-AUD-040 M-02).
    // Never send client-chosen user_id / tenant_id — they were spoofable.
    const payload = {
      message,
      stack: error?.stack || null,
      // NEVER log window.location.href raw. Supabase auth flows carry live
      // credentials in the URL (?token_hash=… on password reset, #access_token=…
      // on implicit flows), and this payload is persisted to client_error_log.
      url: sanitizeUrlForLogging(window.location.href),
      component: extra.component || null,
      user_agent: navigator.userAgent,
    };

    // Fire-and-forget — never await, never throw
    void (async () => {
      const headers = {
        'Content-Type': 'application/json',
        apikey: supabaseAnonKey,
      };
      try {
        const { data } = await supabase.auth.getSession();
        const token = data?.session?.access_token;
        if (typeof token === 'string' && token) {
          headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // Logged-out / session-read failure still reports the error, unattributed.
      }
      fetch(`${supabaseUrl}/functions/v1/log-client-error`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      }).catch(() => {
        // Swallow network errors — a broken error reporter must not cause further errors
      });
    })();
  } catch {
    // Swallow all errors — never throw from reportError
  }
}
